import React, { useState, useEffect } from 'react';
import { fetchCompanies } from '../api';
import Loader from '../components/Loader';

export default function CompaniesPage({ setView, setSelectedCompany, activeFilter, setActiveFilter, totalKahanis, setSelectedRole }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function loadCompanies() {
      setLoading(true);
      try {
        const data = await fetchCompanies({ limit: 50, sortBy: 'totalReviews' });
        setCompanies(data.companies || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        setLoading(false);
      }
    }
    loadCompanies();
  }, []);

  const filtered = search
    ? companies.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()))
    : companies;

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '24px 0', minHeight: 'calc(100vh - 60px)' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 800, margin: '0 0 8px', color: 'var(--text)', letterSpacing: -1 }}>
          Company Heat Map
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 20px' }}>
          {companies.reduce((acc, c) => acc + (c.totalReviews || 0), 0).toLocaleString()} community reports across {companies.length} companies
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflowX: 'auto', flexWrap: 'nowrap' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search company..."
            style={{
              flex: '1 1 120px', minWidth: 0, padding: '8px 14px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--surface)',
              fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--text)', outline: 'none',
            }}
          />
          {totalKahanis !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.8, flexShrink: 0 }} className="mobile-hide">
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Platform Total:</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--blue)', fontFamily: 'var(--mono)' }}>{totalKahanis.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <Loader inline message="Loading companies..." />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          {search ? 'No companies matched your search.' : 'No company data found yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Column Header */}
          <div className="companies-grid-header" style={{
            display: 'grid', gridTemplateColumns: '56px 1fr 100px 100px 32px',
            padding: '6px 20px', gap: 16,
            fontSize: 10, fontWeight: 700, color: 'var(--text3)',
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            <span />
            <span>Company</span>
            <span style={{ textAlign: 'center' }}>👻 Ghost</span>
            <span style={{ textAlign: 'center' }}>✅ Offer</span>
            <span />
          </div>

          {filtered.map((c, i) => (
            <div
              key={c.id || i}
              className="companies-grid-row"
              onClick={() => {
                setSelectedCompany(c.name, 'All');
                if (setSelectedRole) setSelectedRole(null);
                setView('company-reviews');
              }}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
                padding: '16px 20px',
                display: 'grid', gridTemplateColumns: '56px 1fr 100px 100px 32px',
                alignItems: 'center', gap: 16,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = 'var(--blue)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <div className="companies-grid-avatar" style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, border: '1px solid var(--border)' }}>
                {c.name?.charAt(0) || '🏢'}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>{c.totalReviews || 0} reports</div>
              </div>
              <div className="companies-grid-ghost" style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontWeight: 800, fontSize: 13, color: (c.ghostPercent || 0) > 20 ? 'var(--red)' : 'var(--green)' }}>
                  {c.ghostPercent || 0}%
                </div>
                <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase' }}>ghost</div>
              </div>
              <div className="companies-grid-offer" style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontWeight: 800, fontSize: 13, color: (c.offerRate || 0) > 40 ? 'var(--green)' : 'var(--blue)' }}>
                  {c.offerRate || 0}%
                </div>
                <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase' }}>offer</div>
              </div>
              <svg className="companies-grid-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
